import { Auth } from 'aws-amplify';
import EventBus from '@/app/eventBus';
import { API, ENDPOINTS } from '@/app/services/api-url';

const isEmailVerified = user => !!user && user.attributes && user.attributes.email_verified;

const mutations = {
  user(state, user) {
    state.isAuthorized = isEmailVerified(user);
    state.user = user;
  },
  setAction(state, payload) {
    state.hasAction = true;
    state.action = payload;
  },
  clearAction(state) {
    state.hasAction = false;
    state.action = null;
  },
  setError(state, payload) {
    state.hasAuthError = true;
    state.authError = {
      ...payload,
    };
    EventBus.$emit('addNotification', {
      title: 'Authentication Error',
      icon: 'cross',
      message: `${payload.userMessage || 'Unexpected Error: Please try again'}`,
    });
  },
  clearError(state) {
    state.hasAuthError = false;
    state.authError = null;
  },
};

const actions = {
  async login({ dispatch, commit }, { username, password }) {
    try {
      commit('clearError');
      commit('clearAction');
      const user = await Auth.signIn(username, password);
      if (user.challengeName === 'NEW_PASSWORD_REQUIRED') {
        commit('setAction', user);
      } else {
        await dispatch('fetchUser');
      }
    } catch (err) {
      err.userMessage = 'The username and password combination do not match our records. Please check your details and try again';
      if (username !== undefined && username.includes('@')) {
        const postData = {
          email: username.toLowerCase(),
        };
        const res = await API.post(ENDPOINTS.POST_USEREMAILSTATUS, postData);
        if (res.data.data && Object.keys(res.data.data).length > 0) {
          const { data } = res.data;
          data.userEmailStatus.Users.forEach((user) => {
            user.Attributes.forEach((attrs) => {
              if (attrs.Name === 'email_verified' && attrs.Value === 'false') {
                err.userMessage = 'The email address you have entered has not yet been validated. '
                  + 'To continue the registration process please use your username. If you do not recall your username,'
                  + ' please contact Heffron on 1300 433 376';
              }
            });
          });
        }
      }
      if (err.code === 'UserNotConfirmedException') {
        err.userMessage = 'Please confirm your email address to activate your account';
      }
      commit('setError', err);
    }
  },
  async setPassword({ dispatch, commit, state }, { newPassword }) {
    try {
      await Auth.completeNewPassword(state.action, newPassword);
      commit('clearError');
      await dispatch('fetchUser');
    } catch (err) {
      commit('setError', err);
    }
  },
  async signUp({ commit }, {
    username, nickname, givenName, familyName, password, email,
  }) {
    commit('clearError');
    let userInfo;
    try {
      userInfo = await Auth.signUp({
        username,
        password,
        attributes: {
          email,
          nickname,
          given_name: givenName,
          family_name: familyName,
        },
      });
      commit('setAction', {
        confirmSignUp: true,
      });
    } catch (err) {
      err.userMessage = 'Sign up failed. Please try again';
      commit('setError', err);
    }
    return userInfo;
  },
  async confirmSignUp({ commit }, { username, code }) {
    try {
      await Auth.confirmSignUp(
        username,
        code,
      );
      EventBus.$emit('addNotification', {
        title: 'Username Confirmed',
        icon: 'tick',
        message: 'Your username is now confirmed',
      });
      commit('clearError');
      commit('clearAction');
    } catch (err) {
      commit('setError', err);
    }
  },
  async forgotPassword({ dispatch, commit }, { username }) {
    try {
      commit('clearError');
      commit('clearAction');
      await Auth.forgotPassword(username);
    } catch (err) {
      if (err.code === 'InvalidParameterException') {
        await dispatch('resendConfirmationCode', { username });
        err.userMessage = 'Please confirm your email address to activate your account';
        commit('setError', err);
        return;
      }
    }
    EventBus.$emit('addNotification', {
      title: 'Forgot Password',
      icon: 'tick',
      message: 'Reset password request submitted. An email will be sent with a confirmation code to reset your password',
    });
    commit('clearError');
    commit('clearAction');
  },
  async resetPassword({ commit }, { username, confirmCode, newPassword }) {
    try {
      await Auth.forgotPasswordSubmit(
        username,
        confirmCode,
        newPassword,
      );
      EventBus.$emit('addNotification', {
        title: 'Forgot Password',
        icon: 'tick',
        message: 'Your password has been reset. Please sign in',
      });
      commit('clearError');
      commit('clearAction');
    } catch (err) {
      EventBus.$emit('addNotification', {
        title: 'Forgot Password',
        icon: 'cross',
        message: 'Unable to verify your username and code supplied. Please ensure your username and confirmation code from your email are correct',
      });
      commit('setError', err);
    }
  },
  async resendConfirmationCode({ commit }, { username }) {
    commit('clearError');
    commit('clearAction');
    try {
      await Auth.resendSignUp(username);
      commit('setAction', {
        confirmSignUp: true,
      });
    } catch (err) {
      console.log('error resending code: ', err);
    }
  },
  async fetchUser({ commit, dispatch }) {
    try {
      const user = await Auth.currentAuthenticatedUser();
      const expires = user.getSignInUserSession().getIdToken().payload.exp
        - Math.floor(new Date().getTime() / 1000);
      setTimeout(async () => {
        await dispatch('fetchUser');
      }, expires * 1000);
      commit('user', user);
      if (!isEmailVerified(user)) {
        await Auth.verifyCurrentUserAttribute('email');
        commit('setAction', {
          challengeName: 'EMAIL_VERIFICATION_REQUIRED',
        });
      }
    } catch (err) {
      commit('user', null);
      dispatch('clearItem', null, { root: true });
      dispatch('resetFund', null, { root: true });
      dispatch('clearAccounts', null, { root: true });
    }
  },
  async signOut({ commit, dispatch }) {
    try {
      await Auth.signOut();
      commit('user', null);
      dispatch('clearItem', null, { root: true });
      dispatch('resetFund', null, { root: true });
      dispatch('clearBilling', null, { root: true });
      dispatch('clearAccounts', null, { root: true });
    } catch (err) {
      commit('setError', err);
    }
  },
  clearError({ commit }) {
    commit('clearError');
  },
};

const getters = {
  loggedIn(state) {
    return state.isAuthorized;
  },
  hasError(state) {
    return state.hasAuthError;
  },
  hasAction(state) {
    return state.hasAction;
  },
  getUserGivenName(state) {
    if (state.user) {
      return state.user.attributes.given_name;
    }
    return null;
  },
  getUserFamilyName(state) {
    if (state.user) {
      return state.user.attributes.family_name;
    }
    return null;
  },
  getUserEmail(state) {
    if (state.user) {
      return state.user.attributes.email;
    }
    return null;
  },
  getUserNickname(state) {
    if (state.user) {
      return state.user.attributes.nickname;
    }
    return null;
  },
  getAuthError(state) {
    if (state.authError) {
      return state.authError;
    }
    return null;
  },
  getCognitoID(state) {
    if (state.user) {
      return state.user.attributes.sub;
    }
    return null;
  },
  isHeffronUser(state) {
    return state.user.attributes.email.includes('@heffron.com.au');
  },
};

const state = {
  isAuthorized: false,
  user: null,
  hasAuthError: false,
  authError: null,
  hasAction: false,
  action: null,
};

const store = {
  namespaced: true,
  mutations,
  actions,
  getters,
  state,
};
export default store;
